import { Component, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CorporateGroupDetailDto,
  CorporateGroupMergeDto,
  CorporateGroupService,
  TenantFilterDto,
  TenantMergeDto,
  TenantService,
} from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { Validators } from '@angular/forms';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { PendingChanges } from '@guards/pending-changes.guard';
import { routeParam } from '@/utils/signals/router';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';
import { tryWithLoadingSignal } from '@/utils/async-utils';
import { CorporateGroupDeleteDialogComponent } from '@components/dialog/corporate-group-delete-dialog/corporate-group-delete-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-corporate-group-detail-page',
  templateUrl: './corporate-group-detail-page.component.html',
  styleUrls: ['./corporate-group-detail-page.component.scss'],
})
export class CorporateGroupDetailPageComponent implements PendingChanges {

  private readonly router = inject(Router);
  private readonly dialogService = inject(NbDialogService);
  private readonly corporateGroupService = inject(CorporateGroupService);
  private readonly tenantService = inject(TenantService);
  private readonly toastrService = inject(NbToastrService);

  readonly groupId = routeParam('id', true);
  readonly loading = loader();
  readonly saving = loader();
  readonly canEdit = signal(false);
  readonly isEditing = signal(false);
  readonly firstSubmit = signal(false);
  readonly isNew = computed(() => this.groupId() === 'new');

  protected readonly TenantType = TenantFilterDto.TypeEnum;

  readonly group = asyncComputed(() => {
    const id = this.groupId();
    if (id === 'new') return undefined;
    return this.corporateGroupService.findById(id);
  }, undefined, this.loading);

  form?: ReturnType<typeof this.createFormFromDetail>;

  constructor() {
    effect(() => {
      const group = this.group();
      if (this.isNew()) {
        this.isEditing.set(true);
        this.canEdit.set(true);
        this.form = this.createFormFromDetail(null);
        return;
      }
      if (!group) return;
      this.canEdit.set(group.permissions.canEdit);
      this.form = this.createFormFromDetail(group);
      this.form?.disable();
    });
  }

  createFormFromDetail(group: CorporateGroupDetailDto | null) {
    return createForm(({ field }) => ({
      name: field({ value: group?.name ?? '', validators: [Validators.required] }),
      assignedExternalTenantIds: field({ value: group?.assignedExternalTenantIds ?? [] }),
      memberTenantIds: field({ value: (group?.memberTenantIds ?? []) as string[] }),
    }));
  }

  hasPendingChanges(): boolean {
    return this.isEditing() && !!this.form?.dirty;
  }

  onEdit(): void {
    this.isEditing.set(true);
    this.form?.enable();
  }

  cancel(): void {
    this.isEditing.set(false);
    this.form?.disable();
    if (!this.isNew()) {
      this.form = this.createFormFromDetail(this.group() ?? null);
      this.form?.disable();
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit.set(true);
    this.form?.markAsTouched();
    if (!this.form?.valid) return;
    await tryWithLoadingSignal(this.saving, async () => {
      const value = this.form!.value;
      // a firm can only leave by being assigned to ANOTHER corporate group — every internal
      // tenant must keep one (WR-2258), the tenant merge ignores a missing corporateGroupId
      const originalMemberIds = this.isNew() ? [] : (this.group()?.memberTenantIds ?? []);
      const selectedMemberIds: string[] = value.memberTenantIds ?? [];
      const removedMemberIds = originalMemberIds.filter(id => !selectedMemberIds.includes(id));
      if (removedMemberIds.length > 0) {
        this.toastrService.danger(
          'Firmen können hier nicht entfernt werden — eine Firma verlässt den Konzern, indem sie einem anderen Konzern zugewiesen wird.',
          'Konzern',
        );
        return;
      }

      const dto: CorporateGroupMergeDto = {
        id: this.isNew() ? undefined : this.groupId(),
        name: value.name,
        assignedExternalTenantIds: value.assignedExternalTenantIds ?? [],
      };
      const result = await firstValueFrom(this.corporateGroupService.merge(dto));

      const membersToAdd = selectedMemberIds.filter(id => !originalMemberIds.includes(id));
      for (const tenantId of membersToAdd) {
        const tenantDto: TenantMergeDto = {
          id: tenantId,
          tenantType: TenantMergeDto.TenantTypeEnum.Internal,
          corporateGroupId: result.id,
        };
        await firstValueFrom(this.tenantService.merge(tenantDto));
      }

      this.toastrService.success('Gespeichert', 'Erfolg');
      if (this.isNew()) {
        await this.router.navigate(['corporate-group', result.id]);
      } else {
        this.isEditing.set(false);
        this.group.reload();
      }
    });
  }

  async onDelete(): Promise<void> {
    const group = this.group();
    if (!group) return;
    const ref = this.dialogService.open(CorporateGroupDeleteDialogComponent, {
      context: {
        groupName: group.name,
        memberCount: group.memberTenantIds.length,
      },
    });
    const confirmed = await firstValueFrom(ref.onClose);
    if (!confirmed) return;
    try {
      await firstValueFrom(this.corporateGroupService.deleteById(this.groupId()));
      await this.router.navigate(['corporate-groups']);
      this.toastrService.success('Gelöscht', 'Erfolg');
    } catch {
      this.toastrService.danger('Konzern konnte nicht gelöscht werden', 'Fehler');
    }
  }

  async navigateBack(): Promise<void> {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      await this.router.navigate(['corporate-groups']);
    }
  }
}
